import React, { useMemo } from "react";
import { Row, Col, Alert, Container, Card, Image, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import Cardshop from "./Cardshop";

import { Shoptopbar } from "Components/ShopTopBar";
import EmailClothe from "Pages/Catalog/EmailClothe";
import { CommonService } from "Components/CommonService";
import { useGetStoreCartQuery } from "../../redux/services/store/storeCartApi";
import {
  useGetStoreProductBySlugQuery,
  useListStoreProductsQuery,
} from "../../redux/services/store/storeProductsApi";

const SimilarProductsSection = () => {
  const { data: cartData, isLoading: cartLoading, isFetching: cartFetching } = useGetStoreCartQuery();

  const cartItems = (cartData as any)?.items ?? [];
  const firstCartProductSlug = cartItems[0]?.product?.slug || null;

  const { data: firstCartProductDetail } = useGetStoreProductBySlugQuery(
    { slug: firstCartProductSlug as string },
    {
      skip: !firstCartProductSlug,
    }
  );

  const firstCategoryId = firstCartProductDetail?.category_ids?.[0] ?? null;

  const { data: productsData, isLoading: productsLoading, isFetching: productsFetching } =
    useListStoreProductsQuery({
      limit: 8,
      sort: "popular",
      category_id: firstCategoryId,
    });

  const cartProductIds = useMemo(() => {
    return new Set(
      cartItems
        .map((item: any) => Number(item?.product?.product_id))
        .filter((value: number) => Number.isFinite(value))
    );
  }, [cartItems]);

  const similarProducts = useMemo(() => {
    const items = productsData?.items ?? [];
    return items
      .filter((item) => !cartProductIds.has(Number(item.id)))
      .slice(0, 4);
  }, [productsData, cartProductIds]);

  const title = cartItems.length > 0 ? "Similar Products" : "You May Also Like";
  const isBusy = cartLoading || cartFetching || productsLoading || productsFetching;

  if (isBusy) {
    return (
      <section className="section">
        <style>
          {`
            @keyframes cartCatalogSkeletonShimmer {
              0% { background-position: 100% 0; }
              100% { background-position: 0 0; }
            }

            .cart-catalog-skeleton {
              width: 100%;
              background:
                linear-gradient(90deg, rgba(240,240,240,0.95) 25%, rgba(248,248,248,1) 37%, rgba(240,240,240,0.95) 63%);
              background-size: 400% 100%;
              animation: cartCatalogSkeletonShimmer 1.4s ease infinite;
            }

            .catalog-modern-card {
              border: 1px solid rgba(0, 0, 0, 0.06) !important;
              border-radius: 22px !important;
              overflow: hidden;
              background: #fff;
              transition: transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease;
              box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
            }

            .catalog-modern-image-wrap {
              position: relative;
              overflow: hidden;
              background: #f8f9fa;
              border-radius: 22px 22px 0 0;
            }

            .catalog-modern-image-box {
              width: 100%;
              aspect-ratio: 3 / 4;
              overflow: hidden;
              background: #f8f9fa;
            }

            .catalog-modern-body {
              padding: 18px;
            }
          `}
        </style>
        <Container>
          <Row className="justify-content-center">
            <Col lg={12}>
              <div className="d-flex align-items-center justify-content-between mb-4 pb-1">
                <h4 className="flex-grow-1 mb-0">{title}</h4>
              </div>
            </Col>
          </Row>
          <Row>
            {Array.from({ length: 4 }).map((_, index) => (
              <Col xxl={3} lg={4} md={6} key={`similar-skeleton-${index}`}>
                <Card className="catalog-modern-card h-100 border-0 shadow-none overflow-hidden">
                  <div className="catalog-modern-image-wrap">
                    <div className="catalog-modern-image-box">
                      <div
                        className="cart-catalog-skeleton"
                        style={{ height: "100%", minHeight: 220 }}
                      />
                    </div>
                  </div>
                  <Card.Body className="catalog-modern-body d-flex flex-column">
                    <div
                      className="cart-catalog-skeleton"
                      style={{ height: 14, width: "28%", borderRadius: 4, marginBottom: 12 }}
                    />
                    <div
                      className="cart-catalog-skeleton"
                      style={{ height: 18, width: "92%", borderRadius: 4, marginBottom: 8 }}
                    />
                    <div
                      className="cart-catalog-skeleton"
                      style={{ height: 18, width: "68%", borderRadius: 4, marginBottom: 14 }}
                    />
                    <div
                      className="cart-catalog-skeleton"
                      style={{ height: 18, width: "40%", borderRadius: 4, marginBottom: 16 }}
                    />
                    <div
                      className="cart-catalog-skeleton"
                      style={{ height: 42, width: "100%", borderRadius: 999, marginTop: "auto" }}
                    />
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    );
  }

  if (similarProducts.length === 0) {
    return null;
  }

  return (
    <section className="section">
      <style>
        {`
          .catalog-modern-card {
            border: 1px solid rgba(0, 0, 0, 0.06) !important;
            border-radius: 22px !important;
            overflow: hidden;
            background: #fff;
            transition: transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease;
            box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
          }

          .catalog-modern-card:hover {
            transform: translateY(-6px);
            box-shadow: 0 16px 36px rgba(15, 23, 42, 0.12);
            border-color: rgba(0, 0, 0, 0.08) !important;
          }

          .catalog-modern-image-wrap {
            position: relative;
            overflow: hidden;
            background: #f8f9fa;
            border-radius: 22px 22px 0 0;
          }

          .catalog-modern-image-box {
            width: 100%;
            aspect-ratio: 3 / 4;
            overflow: hidden;
            background: #f8f9fa;
          }

          .catalog-modern-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: top center;
            display: block;
            transition: transform 0.5s ease;
          }

          .catalog-modern-card:hover .catalog-modern-image {
            transform: scale(1.06);
          }

          .catalog-modern-discount-badge {
            position: absolute;
            top: 14px;
            left: 14px;
            z-index: 3;
            background: #111;
            color: #fff;
            border-radius: 999px;
            padding: 7px 12px;
            font-size: 12px;
            font-weight: 700;
            line-height: 1;
            letter-spacing: 0.3px;
          }

          .catalog-modern-body {
            padding: 18px;
          }

          .catalog-modern-title {
            color: #111827;
            text-decoration: none;
            font-size: 16px;
            font-weight: 600;
            line-height: 1.35;
            min-height: 43px;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            margin-bottom: 0;
          }

          .catalog-modern-title:hover {
            color: #111827;
          }

          .catalog-modern-price-row {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
            margin-top: 10px;
            margin-bottom: 8px;
          }

          .catalog-modern-price-wrap {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .catalog-modern-price {
            color: #111827;
            font-size: 18px;
            font-weight: 700;
            letter-spacing: -0.01em;
          }

          .catalog-modern-old-price {
            color: #9ca3af;
            text-decoration: line-through;
            font-size: 14px;
            font-weight: 500;
          }

          .catalog-modern-bottom-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-top: 18px;
          }

          .catalog-modern-shop-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            min-height: 42px;
            border-radius: 999px;
            padding: 0.7rem 1.2rem;
            font-weight: 600;
            letter-spacing: 0.01em;
          }
        `}
      </style>
      <Container>
        <Row className="justify-content-center">
          <Col lg={12}>
            <div className="d-flex align-items-center justify-content-between mb-4 pb-1">
              <h4 className="flex-grow-1 mb-0">{title}</h4>
              <div className="flex-shrink-0">
                <Link to="/catalog/clothing" className="link-effect link-primary">
                  Explore More <i className="ri-arrow-right-line ms-1 align-bottom"></i>
                </Link>
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          {similarProducts.map((item) => (
            <Col xxl={3} lg={4} md={6} key={item.id}>
              <Card className="catalog-modern-card h-100 border-0 shadow-none">
                <div className="catalog-modern-image-wrap">
                  {item.compare_at_from &&
                  Number(item.compare_at_from) > Number(item.price_from || 0) ? (
                    <span className="catalog-modern-discount-badge">
                      {Math.round(
                        ((Number(item.compare_at_from) - Number(item.price_from || 0)) /
                          Number(item.compare_at_from)) *
                          100
                      )}
                      % OFF
                    </span>
                  ) : null}
                  <Link to={`/product/${item.slug}`} className="d-block">
                    <div className="catalog-modern-image-box">
                      <Image
                        src={item.image?.cdn_url || "https://via.placeholder.com/240x240.png?text=Product"}
                        alt={item.image?.alt_text || item.name}
                        fluid
                        className="catalog-modern-image"
                      />
                    </div>
                  </Link>
                </div>
                <Card.Body className="catalog-modern-body d-flex flex-column">
                  <Link to={`/product/${item.slug}`} className="catalog-modern-title mt-2">
                    {item.name}
                  </Link>

                  <div className="catalog-modern-price-row">
                    <div className="catalog-modern-price-wrap">
                      <span className="catalog-modern-price">
                        {item.currency_code || "USD"} {item.price_from || "0"}
                      </span>
                      {item.compare_at_from ? (
                        <span className="catalog-modern-old-price">
                          {item.currency_code || "USD"} {item.compare_at_from}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="catalog-modern-bottom-row mt-auto">
                    <Link to={`/product/${item.slug}`} className="btn btn-dark catalog-modern-shop-btn">
                      Shop Now
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

const Shopingcard = () => {
  document.title = "Shop Cart | Manser Commerce";

  const { data: cartData } = useGetStoreCartQuery();
  const cartItems = (cartData as any)?.items ?? [];
  const currency = (cartData as any)?.cart?.currency_code || "INR";
  const totalDiscount = Number((cartData as any)?.totals?.discount ?? 0);
  const totalItems = cartItems.reduce((sum: number, item: any) => {
    const qty = Number(item?.qty ?? 0);
    return sum + (Number.isFinite(qty) ? qty : 0);
  }, 0);

  const promoText = useMemo(() => {
    if (!totalItems) {
      return "Your cart is ready. Add more styles you love before checkout.";
    }

    if (Number.isFinite(totalDiscount) && totalDiscount > 0) {
      const formattedDiscount =
        Number.isInteger(totalDiscount) ? `${totalDiscount}` : totalDiscount.toFixed(2);
      return `You are saving ${currency} ${formattedDiscount} across ${totalItems} item${totalItems === 1 ? "" : "s"} in your cart.`;
    }

    return `You have ${totalItems} item${totalItems === 1 ? "" : "s"} in your cart. Explore similar picks before checkout.`;
  }, [currency, totalDiscount, totalItems]);

  return (
    <React.Fragment>
      <Shoptopbar title="Shopping Cart" page="Shopping Cart" />
      <section className="section">
        <Container>
          <Row>
            <Col lg={12}>
              <Alert className="alert-danger text-center text-capitalize mb-4 fs-14">
                {promoText}
              </Alert>
            </Col>
          </Row>
          <Row className="product-list justify-content-center">
            <Cardshop />
          </Row>
        </Container>
      </section>
      <SimilarProductsSection />
      <EmailClothe />
      <CommonService />
    </React.Fragment>
  );
};

export default Shopingcard;
